/**
 * Validates a category name with support for international characters.
 * 
 * Categories are shorter than names and can be more flexible.
 * 
 * Allowed characters:
 * - Unicode letters (a-z, A-Z, accented characters, non-Latin scripts)
 * - Numbers
 * - Spaces
 * - Common punctuation: hyphens, forward slashes, parentheses (for grouping like "Food (Groceries)")
 * 
 * Rules:
 * - Must not be empty
 * - Must be between 1 and 30 characters
 * - No excessive spaces or special characters in a row
 * 
 * @param value - The category name to validate
 * @param maxLength - Maximum allowed length (default: 30)
 * @returns Error message if invalid, empty string if valid
 */
export const validateCategoryName = (value: string, maxLength: number = 30): string => {
  if (!value.trim()) {
    return "Category name is required.";
  }

  const trimmedValue = value.trim();

  // Check length
  if (trimmedValue.length > maxLength) {
    return `Category name must not exceed ${maxLength} characters.`;
  }

  // Check that category contains only allowed characters
  // Allows:
  // - ASCII letters, digits
  // - Latin Extended characters (À-ÿ)
  // - Spaces, hyphens, slashes, parentheses
  // - Unicode letters (code >= 128)
  const allowedCharRegex = /^[\w\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\s\- /()]+$/i;
  
  if (!allowedCharRegex.test(trimmedValue)) {
    return "Category can contain letters, numbers, spaces, hyphens, slashes, and parentheses.";
  }

  // Check for excessive spaces in a row
  if (/\s{2,}/.test(trimmedValue)) {
    return "Category cannot contain multiple spaces in a row.";
  }

  return "";
};
